import React, { useRef, useEffect, useState } from 'react';
import { FaTicketAlt } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import EventCard from '../../components/EventCard';

/**
 * EventsCarousel - Displays trending events in a horizontal scrollable carousel
 * Extracted from LoyaltyDirectory to reduce file size
 */
function EventsCarousel({ events, onEventClick, hasHydrated, eventPrices = {} }) {
  const { t } = useTranslation();
  const trendingEventsRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Measure scroll states
  useEffect(() => {
    const measureScrollStates = () => {
      const container = trendingEventsRef.current;
      if (!container) return;

      const { scrollLeft, scrollWidth, clientWidth } = container;
      const isAtStart = scrollLeft <= 1;
      const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 1;

      setCanScrollLeft(!isAtStart);
      setCanScrollRight(!isAtEnd);
    };

    measureScrollStates();
    const timer = setTimeout(measureScrollStates, 200);

    window.addEventListener('resize', measureScrollStates);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', measureScrollStates);
    };
  }, [hasHydrated, events]);

  // Auto-scroll carousel
  useEffect(() => {
    if (events.length === 0 || !hasHydrated) {
      return;
    }

    let scrollInterval;
    let isUserInteracting = false;
    let isProgrammaticScroll = false;
    let resumeTimeout;
    let setupTimeout;
    let containerElement = null;

    const setupCarousel = () => {
      containerElement = trendingEventsRef.current;

      if (!containerElement) {
        setupTimeout = setTimeout(setupCarousel, 200);
        return;
      }

      const container = containerElement;

      const getScrollAmount = () => {
        const firstCard = container.querySelector('.event-card');
        if (!firstCard) return 0;

        const cardWidth = firstCard.offsetWidth;
        const containerStyle = window.getComputedStyle(container);
        const gap = parseFloat(containerStyle.gap) || 24;

        return cardWidth + gap;
      };

      container.scrollLeft = 0;

      const handleMouseEnter = () => {
        isUserInteracting = true;
        clearInterval(scrollInterval);
        clearTimeout(resumeTimeout);
      };

      const handleMouseLeave = () => {
        if (isUserInteracting) {
          isUserInteracting = false;
          clearTimeout(resumeTimeout);
          resumeTimeout = setTimeout(() => {
            if (!isUserInteracting) {
              scrollInterval = setInterval(scrollNext, 5000);
            }
          }, 3000);
        }
      };

      const handleTouchStart = () => {
        isUserInteracting = true;
        clearInterval(scrollInterval);
        clearTimeout(resumeTimeout);
      };

      const handleTouchEnd = () => {
        if (isUserInteracting) {
          isUserInteracting = false;
          clearTimeout(resumeTimeout);
          resumeTimeout = setTimeout(() => {
            if (!isUserInteracting) {
              scrollInterval = setInterval(scrollNext, 5000);
            }
          }, 3000);
        }
      };

      const handleScroll = () => {
        if (!isProgrammaticScroll && !isUserInteracting) {
          clearInterval(scrollInterval);
          clearTimeout(resumeTimeout);
          isUserInteracting = true;
          resumeTimeout = setTimeout(() => {
            isUserInteracting = false;
            scrollInterval = setInterval(scrollNext, 5000);
          }, 3000);
        }
      };

      const scrollNext = () => {
        if (!container || isUserInteracting) return;

        const { scrollLeft, scrollWidth, clientWidth } = container;
        const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 5;

        isProgrammaticScroll = true;

        if (isAtEnd) {
          container.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          const scrollAmount = getScrollAmount();
          container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }

        setTimeout(() => {
          isProgrammaticScroll = false;
        }, 600);
      };

      container.addEventListener('mouseenter', handleMouseEnter);
      container.addEventListener('mouseleave', handleMouseLeave);
      container.addEventListener('touchstart', handleTouchStart);
      container.addEventListener('touchend', handleTouchEnd);
      container.addEventListener('scroll', handleScroll);

      scrollInterval = setInterval(scrollNext, 5000);

      return () => {
        clearInterval(scrollInterval);
        clearTimeout(resumeTimeout);
        clearTimeout(setupTimeout);

        if (containerElement) {
          containerElement.removeEventListener('mouseenter', handleMouseEnter);
          containerElement.removeEventListener('mouseleave', handleMouseLeave);
          containerElement.removeEventListener('touchstart', handleTouchStart);
          containerElement.removeEventListener('touchend', handleTouchEnd);
          containerElement.removeEventListener('scroll', handleScroll);
        }
      };
    };

    const initTimeout = setTimeout(setupCarousel, 100);

    return () => {
      clearTimeout(initTimeout);
    };
  }, [hasHydrated, events.length]);

  if (events.length === 0) {
    return null;
  }

  return (
    <div className="perks-category-section trending-events-section">
      <div className="perks-category-header">
        <span className="perks-category-header-icon">
          <FaTicketAlt />
        </span>
        <h2>{t('directory.trendingEvents')}</h2>
        <span className="perks-category-count">({events.length})</span>
      </div>
      <div
        ref={trendingEventsRef}
        className="perks-companies-grid trending-events-grid"
      >
        {events.map((event) => (
          <EventCard
            key={event.event_id}
            event={event}
            onClick={onEventClick}
            initialPrice={eventPrices[event.event_id]}
          />
        ))}
      </div>
    </div>
  );
}

export default EventsCarousel;
